/**
 * MT Strategie — overzichtspagina voor het managementteam.
 *
 * Layout:
 *   - Bronnen-balk  — compacte rij chips direct onder de header
 *   1. Kompas        — strategische pijlers
 *   2. Rocks         — kwartaal-prioriteiten (max 5 in focus)
 *   3. Proposities   — portfolio (6 vaste verticals)
 *   4. Vraag aan MT  — resource-asks per discipline
 *
 * Edit alleen voor MT-emails (zie backend MT_EMAILS). Verberg/disable edit-
 * controls in de UI als de huidige user die rechten niet heeft.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Container } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { useUserRole } from '../../contexts/UserRoleContext';
import PageHeader from '../../ui-components/PageHeader';
import StrategyEditModal from './StrategyEditModal';
import { getPlannerTasks, getPlannerBuckets } from '../../utils/microsoftPlannerService';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { MT_EMAILS, eigenaarVeld, rockKopie, rockSchema, strategieApi } from './strategieApi';
import PropositieVerwijderDialog from './PropositieVerwijderDialog';
import { bevestig } from '../../utils/bevestig';
import { overlegApi } from '../Overleg/overlegApi';
import {
  T, Icon, ROCK_STATUS, STAGE_INFO, URGENCY_INFO, RR_STATUS, DISCIPLINES,
  relativeTime, formatDate,
} from './strategyTokens';

const STRATEGY_URL = `${BACKEND_URL}/Api/strategy`;

export default function Strategy() {
  const { instance } = useMsal();
  const userEmail = (instance.getActiveAccount()?.username || '').toLowerCase();
  const canEdit = MT_EMAILS.has(userEmail);
  const { permissions } = useUserRole();

  const [data, setData] = useState({
    pillars: [], rocks: [], propositions: [], resourceRequests: [],
    links: [], propositionActions: [], config: {},
  });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type, entity }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(STRATEGY_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      console.error('[Strategy] load error:', err);
      toast.error('Kon strategie niet laden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => registreerOngedaanSneltoets(), []);
  // Personenlijst voor de eigenaar-keuzelijst; lukt het niet, dan blijft het een tekstveld.
  const [personen, setPersonen] = useState([]);
  useEffect(() => { overlegApi.personen().then((l) => setPersonen(Array.isArray(l) ? l : [])).catch(() => {}); }, []);

  const handleSave = async (values) => {
    if (!modal) return;
    const { type, entity } = modal;
    setSaving(true);
    try {
      const isNew = !entity?.id;
      const endpoints = {
        pillar:     '/strategy/pillars',
        rock:       '/strategy/rocks',
        proposition: '/strategy/propositions',
        rr:         '/strategy/resource-requests',
        link:       '/strategy/links',
        action:     '/strategy/proposition-actions',
      };
      const path = endpoints[type];
      const url = isNew ? `${BACKEND_URL}/Api${path}` : `${BACKEND_URL}/Api${path}/${entity.id}`;
      const res = await authFetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      toast.success(isNew ? 'Toegevoegd' : 'Bijgewerkt');
      setModal(null);
      load();
    } catch (err) {
      console.error('[Strategy] save error:', err);
      toast.error(err.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  // Verwijderen; propositie en Rock met undo (Joris 2 okt 2026: propositie verwijderen gaf 'Verwijderen mislukt'
  // omdat de route niet bestond). Propositie met actiepunten → keuze verplaatsen/mee verwijderen.
  const [propKeuze, setPropKeuze] = useState(null); // { propositie, aantal, openAantal }

  // Undo: zijn verplaatste actiepunten intussen verdwenen, dan eerst vragen (de server herstelt anders niets).
  const herstelPropositie = async (herstel) => {
    try {
      await strategieApi.propositieHerstel(herstel);
    } catch (e) {
      if (!(e.status === 409 && e.body?.code === 'acties_weg')) throw e;
      if (!(await bevestig({ titel: 'Niet alles is terug te zetten', tekst: e.message, knop: 'Toch herstellen' }))) throw new Error('niet hersteld');
      await strategieApi.propositieHerstel({ ...herstel, ook_zonder: true });
    }
    load();
  };

  const verwijderPropositie = async (prop, keuze = {}) => {
    setSaving(true);
    try {
      const r = await strategieApi.propositieVerwijder(prop.id, keuze);
      setModal(null); setPropKeuze(null);
      load();
      biedOngedaanAan(`Propositie '${prop.name}' verwijderd`, () => herstelPropositie(r.herstel), { eigenKnop: true });
    } catch (err) {
      if (err.status === 409 && err.body?.code === 'heeft_acties') {
        setPropKeuze({ propositie: prop, aantal: err.body.acties || 0, openAantal: err.body.open_acties || 0 });
      } else {
        toast.error(err.message ? `Verwijderen mislukt: ${err.message}` : 'Verwijderen mislukt');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!modal?.entity?.id) return;
    if (modal.type === 'proposition') { await verwijderPropositie(modal.entity); return; }
    const endpoints = {
      pillar: '/strategy/pillars', rock: '/strategy/rocks',
      rr: '/strategy/resource-requests',
      link: '/strategy/links', action: '/strategy/proposition-actions',
    };
    const { type, entity } = modal;
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api${endpoints[type]}/${entity.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setModal(null);
      load();
      if (type === 'rock') {
        biedOngedaanAan(`Rock '${entity.title}' verwijderd`, async () => { await strategieApi.rockNieuw(rockKopie(entity)); load(); }, { eigenKnop: true });
      } else {
        toast.success('Verwijderd');
      }
    } catch (err) {
      console.error('[Strategy] delete error:', err);
      toast.error(err.message ? `Verwijderen mislukt: ${err.message}` : 'Verwijderen mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: 1800, mx: 'auto', py: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
      </Container>
    );
  }

  // Hard-gate UI: als de user geen permissie heeft tonen we niets dan kort bericht.
  // ProtectedRoute zou ze normaal al wegsturen; dit is een laatste vangnet.
  if (!permissions.strategy?.view) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: 1800, mx: 'auto', py: 5 }}>
        <Typography sx={{ fontFamily: T.fontBody, color: T.outline }}>
          Geen toegang tot de strategiepagina.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1800, mx: 'auto', py: 4, px: { xs: 2, md: 4 } }}>
      <PageHeader
        icon="explore"
        title="Strategie"
        subtitle="MT-overzicht van visie, Rocks, proposities en wat we nodig hebben"
        accent={{ color: T.tertiary, bg: T.tertiaryContainer }}
      />

      <BronnenBar
        links={data.links}
        canEdit={canEdit}
        onEdit={(entity) => setModal({ type: 'link', entity })}
        onAdd={() => setModal({ type: 'link', entity: null })}
      />

      <MtPlannerWidget
        config={data.config}
        canEdit={canEdit}
        msalInstance={instance}
        onConfigChange={load}
      />

      <KompasSection
        pillars={data.pillars}
        canEdit={canEdit}
        onEdit={(entity) => setModal({ type: 'pillar', entity })}
        onAdd={() => setModal({ type: 'pillar', entity: null })}
      />

      <RocksSection
        rocks={data.rocks}
        pillars={data.pillars}
        canEdit={canEdit}
        onEdit={(entity) => setModal({ type: 'rock', entity })}
        onAdd={() => setModal({ type: 'rock', entity: null })}
      />

      <PropositiesSection
        propositions={data.propositions}
        actions={data.propositionActions}
        canEdit={canEdit}
        onEditProposition={(entity) => setModal({ type: 'proposition', entity })}
        onEditAction={(entity) => setModal({ type: 'action', entity })}
        onAddAction={(propId) => setModal({ type: 'action', entity: { proposition_id: propId } })}
        onToggleActionStatus={async (action) => {
          const next = action.status === 'done' ? 'open' : 'done';
          try {
            const res = await authFetch(`${BACKEND_URL}/Api/strategy/proposition-actions/${action.id}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: next }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            load();
            biedOngedaanAan(next === 'done' ? 'Actie afgevinkt' : 'Actie heropend', async () => {
              const r = await authFetch(`${BACKEND_URL}/Api/strategy/proposition-actions/${action.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: action.status || 'open' }),
              });
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              load();
            });
          } catch (err) {
            toast.error('Bijwerken mislukt');
          }
        }}
      />

      <VraagAanMtSection
        items={data.resourceRequests}
        canEdit={canEdit}
        onEdit={(entity) => setModal({ type: 'rr', entity })}
        onAdd={(discipline) => setModal({ type: 'rr', entity: { discipline } })}
      />

      {modal && (
        <StrategyEditModal
          open={!!modal}
          onClose={() => setModal(null)}
          title={modalTitle(modal)}
          schema={modalSchema(modal, data, personen)}
          entity={modal.entity}
          onSave={handleSave}
          onDelete={modal.entity?.id ? handleDelete : undefined}
          saving={saving}
        />
      )}
      <PropositieVerwijderDialog
        open={!!propKeuze}
        propositie={propKeuze?.propositie}
        aantal={propKeuze?.aantal}
        openAantal={propKeuze?.openAantal}
        anderen={(data.propositions || []).filter((p) => p.id !== propKeuze?.propositie?.id)}
        onClose={() => setPropKeuze(null)}
        onKies={(keuze) => verwijderPropositie(propKeuze.propositie, keuze)}
      />
    </Container>
  );
}

// ─── Sectie wrapper ──────────────────────────────────────────────────

function SectionHeader({ eyebrow, title, subtitle, action }) {
  return (
    <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
      <Box>
        {eyebrow && (
          <Typography sx={{
            fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.16em', color: T.outlineVariant, mb: 0.5,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography sx={{ fontFamily: T.font, fontSize: '1.4rem', fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline, mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

function Section({ children, sx = {} }) {
  return <Box sx={{ mb: 6, ...sx }}>{children}</Box>;
}

function AddButton({ label, onClick }) {
  return (
    <Box
      onClick={onClick} role="button" tabIndex={0}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer',
        px: 1.5, py: 0.75, borderRadius: '0.5rem', bgcolor: T.tertiaryContainer,
        color: T.tertiary, fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem',
        userSelect: 'none', transition: 'all 0.12s',
        '&:hover': { bgcolor: '#cec0f5' },
      }}
    >
      <Icon name="add" size={16} fill /> {label}
    </Box>
  );
}

function Card({ children, onClick, accent, hover = true, sx = {} }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5,
        border: `1px solid ${T.surfaceHigh}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s',
        '&:hover': hover && onClick ? {
          boxShadow: '0 8px 24px rgba(45,51,53,0.07)',
          borderColor: accent ? `${accent}40` : T.outlineVariant,
          transform: 'translateY(-2px)',
        } : {},
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function StatusPill({ info, size = 'sm' }) {
  const fs = size === 'sm' ? '0.65rem' : '0.75rem';
  const px = size === 'sm' ? 1 : 1.25;
  const py = size === 'sm' ? 0.25 : 0.4;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px, py, borderRadius: '999px', bgcolor: info.bg, color: info.color,
      fontFamily: T.fontBody, fontWeight: 700, fontSize: fs,
      textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>
      {info.label}
    </Box>
  );
}

// ─── 1. KOMPAS ───────────────────────────────────────────────────────

function KompasSection({ pillars, canEdit, onEdit, onAdd }) {
  return (
    <Section>
      <SectionHeader
        eyebrow="01 · Kompas"
        title="Waar we naartoe werken"
        subtitle="Strategische prioriteiten die alles wat we doen richten."
        action={canEdit && <AddButton label="Prioriteit" onClick={onAdd} />}
      />
      {pillars.length === 0 ? (
        <Card hover={false} sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ fontFamily: T.fontBody, color: T.outline, fontSize: '0.85rem' }}>
            Nog geen prioriteiten. {canEdit && 'Klik "Prioriteit" om de eerste toe te voegen.'}
          </Typography>
        </Card>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}>
          {pillars.map((p, idx) => (
            <Card key={p.id} onClick={canEdit ? () => onEdit(p) : undefined} accent={T.tertiary}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '0.5rem',
                  bgcolor: T.tertiaryContainer, color: T.tertiary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: T.font, fontWeight: 800, fontSize: '0.85rem',
                }}>
                  {idx + 1}
                </Box>
                <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1rem', color: T.text }}>
                  {p.title}
                </Typography>
              </Box>
              {p.description && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.secondary, lineHeight: 1.5 }}>
                  {p.description}
                </Typography>
              )}
            </Card>
          ))}
        </Box>
      )}
    </Section>
  );
}

// ─── 2. ROCKS ────────────────────────────────────────────────────────

function RocksSection({ rocks, pillars, canEdit, onEdit, onAdd }) {
  return (
    <Section>
      <SectionHeader
        eyebrow="02 · Dit kwartaal"
        title="Rocks"
        subtitle={`${rocks.length} van max 5 in focus. Update minimaal tweewekelijks.`}
        action={canEdit && <AddButton label="Rock" onClick={onAdd} />}
      />
      {rocks.length === 0 ? (
        <Card hover={false} sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ fontFamily: T.fontBody, color: T.outline, fontSize: '0.85rem' }}>
            Nog geen Rocks. {canEdit && 'Klik "Rock" om de eerste te plaatsen.'}
          </Typography>
        </Card>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}>
          {rocks.map((r) => <RockCard key={r.id} rock={r} pillars={pillars} canEdit={canEdit} onEdit={onEdit} />)}
        </Box>
      )}
    </Section>
  );
}

function RockCard({ rock, pillars, canEdit, onEdit }) {
  const status = ROCK_STATUS[rock.status] || ROCK_STATUS.on_track;
  const pillar = pillars.find((p) => p.id === rock.pillar_id);
  const pct = Math.max(0, Math.min(100, Number(rock.progress_pct) || 0));

  return (
    <Card onClick={canEdit ? () => onEdit(rock) : undefined} accent={status.color}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1rem', color: T.text, flex: 1, minWidth: 0 }}>
          {rock.title}
        </Typography>
        <StatusPill info={status} />
      </Box>
      {rock.description && (
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary,
          lineHeight: 1.5, mb: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {rock.description}
        </Typography>
      )}

      {/* Progress bar */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, fontWeight: 600 }}>
            Voortgang
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.text, fontWeight: 700 }}>
            {pct}%
          </Typography>
        </Box>
        <Box sx={{ width: '100%', height: 6, bgcolor: T.surfaceLow, borderRadius: 999, overflow: 'hidden' }}>
          <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: status.color, transition: 'width 0.3s' }} />
        </Box>
      </Box>

      {/* Meta-row */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        {rock.owner_email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="person" size={14} sx={{ color: T.outline }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>
              {rock.owner_email.split('@')[0]}
            </Typography>
          </Box>
        )}
        {rock.deadline && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="event" size={14} sx={{ color: T.outline }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>
              {formatDate(rock.deadline)}
            </Typography>
          </Box>
        )}
        {pillar && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon name="flag" size={14} sx={{ color: T.tertiary }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.tertiary, fontWeight: 600 }}>
              {pillar.title}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Last update */}
      <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${T.surfaceHigh}` }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant, fontStyle: 'italic' }}>
          {relativeTime(rock.updated_at)}
        </Typography>
        {rock.last_update_note && (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, mt: 0.5 }}>
            "{rock.last_update_note}"
          </Typography>
        )}
      </Box>
    </Card>
  );
}

// ─── 3. PROPOSITIES ──────────────────────────────────────────────────

function PropositiesSection({
  propositions, actions, canEdit,
  onEditProposition, onEditAction, onAddAction, onToggleActionStatus,
}) {
  const actionsByProp = useMemo(() => {
    const m = new Map();
    actions.forEach((a) => {
      if (!m.has(a.proposition_id)) m.set(a.proposition_id, []);
      m.get(a.proposition_id).push(a);
    });
    return m;
  }, [actions]);

  return (
    <Section>
      <SectionHeader
        eyebrow="03 · Portfolio"
        title="Proposities"
        subtitle="De zes pijlers waarmee we de markt bedienen. Met actiepunten per pijler."
      />
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 2,
      }}>
        {propositions.map((p) => (
          <PropositieCard
            key={p.id}
            proposition={p}
            actions={actionsByProp.get(p.id) || []}
            canEdit={canEdit}
            onEdit={onEditProposition}
            onEditAction={onEditAction}
            onAddAction={onAddAction}
            onToggleActionStatus={onToggleActionStatus}
          />
        ))}
      </Box>
    </Section>
  );
}

function PropositieCard({ proposition, actions, canEdit, onEdit, onEditAction, onAddAction, onToggleActionStatus }) {
  const stage = STAGE_INFO[proposition.stage] || STAGE_INFO.in_ontwikkeling;
  const onCardClick = (e) => {
    if (e.target.closest('[data-action-zone]')) return;
    if (canEdit) onEdit(proposition);
  };
  return (
    <Card onClick={canEdit ? onCardClick : undefined} accent={stage.color} sx={{
      borderTop: proposition.mt_focus ? `3px solid ${T.tertiary}` : undefined,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1rem', color: T.text, mb: 0.25 }}>
            {proposition.name}
          </Typography>
          {proposition.owner_email && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
              {proposition.owner_email.split('@')[0]}
            </Typography>
          )}
        </Box>
        <StatusPill info={stage} />
      </Box>

      {/* Stage indicator */}
      <Box sx={{ display: 'flex', gap: 0.4, mb: 1.5 }}>
        {[1, 2, 3, 4, 5].map((step) => (
          <Box key={step} sx={{
            flex: 1, height: 4, borderRadius: 999,
            bgcolor: step <= stage.step ? stage.color : T.surfaceLow,
          }} />
        ))}
      </Box>

      {proposition.kpi_label && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '1.4rem', color: T.text }}>
            {proposition.kpi_value || '—'}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
            {proposition.kpi_label}
          </Typography>
        </Box>
      )}

      {proposition.description && (
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {proposition.description}
        </Typography>
      )}

      {proposition.roadmap_note && (
        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${T.surfaceHigh}` }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.4 }}>
            Roadmap
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary }}>
            {proposition.roadmap_note}
          </Typography>
        </Box>
      )}

      {proposition.mt_focus && (
        <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Icon name="bolt" size={14} fill sx={{ color: T.tertiary }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            MT-focus dit kwartaal
          </Typography>
        </Box>
      )}

      {/* Actiepunten-zone */}
      <Box data-action-zone sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${T.surfaceHigh}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography sx={{
            fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em', color: T.outline,
          }}>
            Acties{actions.length > 0 ? ` · ${actions.filter((a) => a.status !== 'done').length} open` : ''}
          </Typography>
          {canEdit && (
            <Box
              onClick={(e) => { e.stopPropagation(); onAddAction(proposition.id); }}
              role="button" tabIndex={0}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
                px: 0.75, py: 0.25, borderRadius: '0.35rem',
                color: T.tertiary, cursor: 'pointer', fontFamily: T.fontBody,
                fontSize: '0.7rem', fontWeight: 700,
                '&:hover': { bgcolor: T.tertiaryContainer },
              }}
            >
              <Icon name="add" size={12} /> Actie
            </Box>
          )}
        </Box>
        {actions.length === 0 ? (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outlineVariant, fontStyle: 'italic' }}>
            Nog geen actiepunten.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            {actions.slice(0, 5).map((a) => (
              <ActionRow
                key={a.id}
                action={a}
                canEdit={canEdit}
                onEdit={onEditAction}
                onToggleStatus={onToggleActionStatus}
              />
            ))}
            {actions.length > 5 && (
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant, fontStyle: 'italic', pl: 2.5 }}>
                +{actions.length - 5} meer
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Card>
  );
}

function ActionRow({ action, canEdit, onEdit, onToggleStatus }) {
  const done = action.status === 'done';
  const inProgress = action.status === 'in_progress';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Box
        onClick={(e) => { e.stopPropagation(); if (canEdit) onToggleStatus(action); }}
        sx={{
          width: 16, height: 16, flexShrink: 0,
          borderRadius: '50%',
          border: `1.5px solid ${done ? T.emerald : inProgress ? T.blue : T.outlineVariant}`,
          bgcolor: done ? T.emerald : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: canEdit ? 'pointer' : 'default',
          transition: 'all 0.12s',
          '&:hover': canEdit ? { borderColor: done ? T.emerald : T.tertiary } : {},
        }}
      >
        {done && <Icon name="check" size={10} sx={{ color: '#fff' }} />}
      </Box>
      <Typography
        onClick={(e) => { e.stopPropagation(); if (canEdit) onEdit(action); }}
        sx={{
          fontFamily: T.fontBody, fontSize: '0.78rem',
          color: done ? T.outlineVariant : T.text,
          textDecoration: done ? 'line-through' : 'none',
          flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          cursor: canEdit ? 'pointer' : 'default',
          '&:hover': canEdit ? { color: T.tertiary } : {},
        }}
      >
        {action.title}
      </Typography>
      {action.owner_email && (
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, flexShrink: 0,
        }}>
          {action.owner_email.split('@')[0]}
        </Typography>
      )}
    </Box>
  );
}

// ─── 4. VRAAG AAN MT ─────────────────────────────────────────────────

function VraagAanMtSection({ items, canEdit, onEdit, onAdd }) {
  const byDiscipline = useMemo(() => {
    const m = { sales: [], marketing: [], hr: [] };
    items.forEach((it) => {
      if (m[it.discipline]) m[it.discipline].push(it);
    });
    return m;
  }, [items]);

  return (
    <Section>
      <SectionHeader
        eyebrow="04 · Vraag aan MT"
        title="Wat we nodig hebben"
        subtitle="Concrete asks per discipline. Open items wachten op MT-beslissing."
      />
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 2,
      }}>
        {DISCIPLINES.map((d) => (
          <DisciplineColumn
            key={d.key}
            discipline={d}
            items={byDiscipline[d.key]}
            canEdit={canEdit}
            onEdit={onEdit}
            onAdd={() => onAdd(d.key)}
          />
        ))}
      </Box>
    </Section>
  );
}

function DisciplineColumn({ discipline, items, canEdit, onEdit, onAdd }) {
  const openCount = items.filter((i) => i.status === 'open' || i.status === 'in_progress').length;
  return (
    <Box sx={{
      bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5,
      border: `1px solid ${T.surfaceHigh}`,
      display: 'flex', flexDirection: 'column', gap: 1.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '0.5rem',
          bgcolor: discipline.bg, color: discipline.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={discipline.icon} size={20} fill />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1.05rem', color: T.text }}>
            {discipline.label}
          </Typography>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
            {openCount} open · {items.length} totaal
          </Typography>
        </Box>
        {canEdit && (
          <Box
            onClick={onAdd}
            sx={{
              width: 28, height: 28, borderRadius: '0.4rem',
              bgcolor: T.surfaceLow, color: T.secondary, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&:hover': { bgcolor: discipline.bg, color: discipline.color },
            }}
          >
            <Icon name="add" size={16} />
          </Box>
        )}
      </Box>
      {items.length === 0 && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant, fontStyle: 'italic', py: 1 }}>
          Geen open vragen.
        </Typography>
      )}
      {items.map((it) => <ResourceRequestItem key={it.id} item={it} canEdit={canEdit} onEdit={onEdit} />)}
    </Box>
  );
}

function ResourceRequestItem({ item, canEdit, onEdit }) {
  const status = RR_STATUS[item.status] || RR_STATUS.open;
  const urgency = URGENCY_INFO[item.urgency] || URGENCY_INFO.normal;
  return (
    <Box
      onClick={canEdit ? () => onEdit(item) : undefined}
      sx={{
        bgcolor: T.surfaceLow, borderRadius: '0.5rem', p: 1.5,
        cursor: canEdit ? 'pointer' : 'default',
        borderLeft: `3px solid ${urgency.color}`,
        '&:hover': canEdit ? { bgcolor: '#eaf0f2' } : {},
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', fontWeight: 600, color: T.text, flex: 1, minWidth: 0 }}>
          {item.title}
        </Typography>
        <StatusPill info={status} />
      </Box>
      {item.description && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.45, mb: 0.5 }}>
          {item.description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {item.owner_email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Icon name="person" size={12} sx={{ color: T.outline }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.secondary, fontWeight: 600 }}>
              {item.owner_email.split('@')[0]}
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant, ml: 'auto' }}>
          {item.needed_by ? `Nodig: ${formatDate(item.needed_by)}` : relativeTime(item.updated_at)}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── BRONNEN-BALK (compacte rij chips direct onder de header) ────────

function BronnenBar({ links, canEdit, onEdit, onAdd }) {
  if (!links.length && !canEdit) return null;
  return (
    <Box sx={{
      display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center',
      mb: 3, mt: -1,
    }}>
      {links.map((l) => (
        <LinkChip key={l.id} link={l} canEdit={canEdit} onEdit={onEdit} />
      ))}
      {canEdit && (
        <Box
          onClick={onAdd} role="button" tabIndex={0}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.4,
            px: 1, py: 0.5, borderRadius: '999px',
            border: `1px dashed ${T.outlineVariant}`,
            color: T.outline, fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600,
            cursor: 'pointer', userSelect: 'none',
            '&:hover': { color: T.tertiary, borderColor: T.tertiary },
          }}
        >
          <Icon name="add" size={14} /> Link
        </Box>
      )}
    </Box>
  );
}

function LinkChip({ link, canEdit, onEdit }) {
  const [hover, setHover] = useState(false);
  const open = () => window.open(link.url, '_blank', 'noopener,noreferrer');
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        if (e.target.closest('[data-link-edit]')) return;
        open();
      }}
      title={link.description || link.url}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: 1.1, py: 0.5, borderRadius: '999px',
        bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`,
        color: T.secondary, fontFamily: T.fontBody, fontSize: '0.75rem', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.12s', userSelect: 'none',
        '&:hover': { borderColor: T.tertiary, color: T.tertiary, bgcolor: T.tertiaryContainer },
      }}
    >
      <Icon name={link.icon || 'link'} size={14} fill sx={{ color: 'inherit' }} />
      <span>{link.title}</span>
      <Icon
        name="open_in_new" size={12}
        sx={{ color: T.outlineVariant, opacity: hover ? 1 : 0.5 }}
      />
      {canEdit && hover && (
        <Box
          data-link-edit
          onClick={(e) => { e.stopPropagation(); onEdit(link); }}
          sx={{
            ml: 0.25, display: 'inline-flex', alignItems: 'center',
            color: T.outline, '&:hover': { color: T.tertiary },
          }}
        >
          <Icon name="edit" size={12} />
        </Box>
      )}
    </Box>
  );
}

// ─── MT PLANNER WIDGET (read-only Graph API) ─────────────────────────

function MtPlannerWidget({ config, canEdit, msalInstance, onConfigChange }) {
  const planId = config?.mt_planner_plan_id || '';
  const bucketName = config?.mt_planner_bucket_name || 'Actiepunten,Korte WVTTK';

  const [tasks, setTasks] = useState(null);   // null = niet geladen
  const [bucketNameById, setBucketNameById] = useState(new Map());
  const [allowedBucketIds, setAllowedBucketIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [setupPlanId, setSetupPlanId] = useState(planId);
  const [setupBucketName, setSetupBucketName] = useState(bucketName);
  const [savingSetup, setSavingSetup] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    setError(null);
    try {
      const [buckets, allTasks] = await Promise.all([
        getPlannerBuckets(msalInstance, planId),
        getPlannerTasks(msalInstance, planId),
      ]);
      const wanted = bucketName.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      const matching = buckets.filter((b) => wanted.includes(b.name?.toLowerCase()));
      setAllowedBucketIds(new Set(matching.map((b) => b.id)));
      setBucketNameById(new Map(buckets.map((b) => [b.id, b.name])));
      setTasks(allTasks);
    } catch (err) {
      console.error('[MtPlannerWidget] fetch error:', err);
      setError(err.message || 'Planner ophalen mislukt');
    } finally {
      setLoading(false);
    }
  }, [planId, bucketName, msalInstance]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const visible = useMemo(() => {
    if (!tasks) return [];
    const filtered = allowedBucketIds.size
      ? tasks.filter((t) => allowedBucketIds.has(t.bucketId))
      : tasks;
    return filtered
      .filter((t) => t.percentComplete < 100)
      .sort((a, b) => (a.orderHint || '').localeCompare(b.orderHint || ''));
  }, [tasks, allowedBucketIds]);

  const saveConfig = async () => {
    setSavingSetup(true);
    try {
      await Promise.all([
        authFetch(`${BACKEND_URL}/Api/strategy/config/mt_planner_plan_id`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: setupPlanId.trim() || null }),
        }),
        authFetch(`${BACKEND_URL}/Api/strategy/config/mt_planner_bucket_name`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: setupBucketName.trim() || null }),
        }),
      ]);
      toast.success('Planner-config opgeslagen');
      setShowSetup(false);
      onConfigChange?.();
    } catch (err) {
      toast.error('Opslaan mislukt');
    } finally {
      setSavingSetup(false);
    }
  };

  // Setup-mode (geen plan-ID geconfigureerd of MT klikte tandwiel)
  if (!planId || showSetup) {
    if (!canEdit && !planId) return null; // niet-MT zonder config: widget verbergen
    return (
      <Box sx={{
        mb: 3, p: 2, borderRadius: '0.6rem',
        bgcolor: T.surfaceLow, border: `1px dashed ${T.outlineVariant}`,
      }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, mb: 1 }}>
          <Icon name="info" size={14} sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          MT Planner-widget configureren. Plan-ID vind je in de Planner-URL (na <code>/plan/</code>). Meerdere buckets? Scheid met komma's.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text" value={setupPlanId} placeholder="planId"
            onChange={(e) => setSetupPlanId(e.target.value)}
            style={{ flex: '1 1 320px', minWidth: 0, padding: '6px 10px', fontFamily: T.fontBody,
                     fontSize: '0.82rem', border: `1px solid ${T.surfaceHigh}`, borderRadius: 6 }}
          />
          <input
            type="text" value={setupBucketName} placeholder="bucket-naam"
            onChange={(e) => setSetupBucketName(e.target.value)}
            style={{ flex: '0 0 180px', padding: '6px 10px', fontFamily: T.fontBody,
                     fontSize: '0.82rem', border: `1px solid ${T.surfaceHigh}`, borderRadius: 6 }}
          />
          <Box
            onClick={savingSetup ? undefined : saveConfig}
            role="button" tabIndex={0}
            sx={{
              px: 1.5, py: 0.7, borderRadius: '0.4rem',
              bgcolor: T.tertiary, color: '#fff',
              fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 700,
              cursor: savingSetup ? 'wait' : 'pointer', opacity: savingSetup ? 0.6 : 1,
            }}
          >
            {savingSetup ? 'Opslaan…' : 'Opslaan'}
          </Box>
          {planId && (
            <Box
              onClick={() => setShowSetup(false)} role="button" tabIndex={0}
              sx={{ px: 1, py: 0.7, color: T.outline, cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.78rem' }}
            >
              Annuleer
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  const countLabel = loading
    ? 'laden…'
    : error
      ? 'fout bij laden'
      : `${visible.length} open`;

  return (
    <Box sx={{
      mb: 3, borderRadius: '0.6rem',
      bgcolor: T.surface, border: `1px solid ${T.surfaceHigh}`,
    }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        role="button" tabIndex={0}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 2, py: 1.25, cursor: 'pointer',
          '&:hover': { bgcolor: T.surfaceLow },
          borderRadius: expanded ? '0.6rem 0.6rem 0 0' : '0.6rem',
        }}
      >
        <Icon name="task_alt" size={18} fill sx={{ color: T.tertiary }} />
        <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '0.9rem', color: T.text }}>
          MT-actiepunten
        </Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outline }}>
          · {countLabel}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box
          onClick={(e) => { e.stopPropagation(); fetchTasks(); }}
          role="button" tabIndex={0}
          title="Vernieuwen"
          sx={{ color: T.outline, cursor: 'pointer', display: 'flex', '&:hover': { color: T.tertiary } }}
        >
          <Icon name="refresh" size={16} />
        </Box>
        {canEdit && (
          <Box
            onClick={(e) => { e.stopPropagation(); setShowSetup(true); setSetupPlanId(planId); setSetupBucketName(bucketName); }}
            role="button" tabIndex={0}
            title="Configureren"
            sx={{ color: T.outline, cursor: 'pointer', display: 'flex', '&:hover': { color: T.tertiary } }}
          >
            <Icon name="settings" size={16} />
          </Box>
        )}
        <Icon
          name={expanded ? 'expand_less' : 'expand_more'} size={18}
          sx={{ color: T.outline }}
        />
      </Box>

      {expanded && (
        <Box sx={{ px: 2, pb: 1.5, pt: 0, borderTop: `1px solid ${T.surfaceHigh}` }}>
          {error && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.red, mt: 1 }}>
              {error}
            </Typography>
          )}
          {!loading && !error && visible.length === 0 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant, fontStyle: 'italic', mt: 1 }}>
              Geen open actiepunten in {bucketName}.
            </Typography>
          )}
          {!loading && !error && visible.length > 0 && (
            <Box sx={{
              mt: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 0.75,
            }}>
              {visible.slice(0, 12).map((t) => (
                <PlannerTaskRow key={t.id} task={t} bucketName={bucketNameById.get(t.bucketId)} />
              ))}
            </Box>
          )}
          {!loading && !error && visible.length > 12 && (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant, mt: 1 }}>
              +{visible.length - 12} meer. Open in Teams voor het volledige bord.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function PlannerTaskRow({ task, bucketName }) {
  const due = task.dueDateTime ? new Date(task.dueDateTime) : null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0,
      px: 1, py: 0.55, borderRadius: '0.35rem', bgcolor: T.surfaceLow,
    }}>
      <Icon name="radio_button_unchecked" size={14} sx={{ color: T.outlineVariant, flexShrink: 0 }} />
      <Typography sx={{
        fontFamily: T.fontBody, fontSize: '0.78rem', color: T.text, flex: 1, minWidth: 0,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {task.title}
      </Typography>
      {bucketName && (
        <Typography sx={{
          fontFamily: T.fontBody, fontSize: '0.62rem', color: T.outline, flexShrink: 0,
          textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700,
        }}>
          {bucketName}
        </Typography>
      )}
      {due && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outline, flexShrink: 0 }}>
          {due.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
        </Typography>
      )}
    </Box>
  );
}

// ─── Modal schema helpers ────────────────────────────────────────────

function modalTitle(modal) {
  const isNew = !modal.entity?.id;
  const verb = isNew ? 'Nieuwe' : 'Bewerk';
  const labels = {
    pillar: 'prioriteit', rock: 'Rock',
    proposition: 'propositie', rr: 'vraag aan MT', link: 'link', action: 'actiepunt',
  };
  return `${verb} ${labels[modal.type]}`;
}

function modalSchema(modal, data, personen = []) {
  const huidig = modal.entity?.owner_email;
  if (modal.type === 'pillar') {
    return [
      { name: 'title', label: 'Titel', type: 'text', required: true },
      { name: 'description', label: 'Beschrijving', type: 'textarea', rows: 3 },
      { name: 'sort_order', label: 'Volgorde', type: 'number', defaultValue: 0 },
    ];
  }
  if (modal.type === 'rock') return rockSchema(data.pillars, personen, huidig);
  if (modal.type === 'proposition') {
    return [
      { name: 'name', label: 'Naam', type: 'text', required: true },
      { name: 'description', label: 'Beschrijving', type: 'textarea', rows: 3 },
      eigenaarVeld('Eigenaar', personen, huidig),
      {
        name: 'stage', label: 'Fase', type: 'select', required: true,
        options: Object.entries(STAGE_INFO).map(([k, v]) => ({ value: k, label: v.label })),
        defaultValue: 'in_ontwikkeling',
      },
      { name: 'kpi_label', label: 'KPI-label (bv. "lopende projecten")', type: 'text' },
      { name: 'kpi_value', label: 'KPI-waarde (bv. "12")', type: 'text' },
      { name: 'roadmap_note', label: 'Roadmap-notitie', type: 'textarea', rows: 2 },
      { name: 'mt_focus', label: 'MT-focus dit kwartaal', type: 'boolean', defaultValue: false },
      { name: 'sort_order', label: 'Volgorde', type: 'number', defaultValue: 0 },
    ];
  }
  if (modal.type === 'rr') {
    return [
      {
        name: 'discipline', label: 'Discipline', type: 'select', required: true,
        options: DISCIPLINES.map((d) => ({ value: d.key, label: d.label })),
        defaultValue: modal.entity?.discipline || 'sales',
      },
      { name: 'title', label: 'Vraag', type: 'text', required: true },
      { name: 'description', label: 'Toelichting', type: 'textarea', rows: 3 },
      eigenaarVeld('Verantwoordelijke', personen, huidig),
      {
        name: 'urgency', label: 'Urgentie', type: 'select', required: true,
        options: Object.entries(URGENCY_INFO).map(([k, v]) => ({ value: k, label: v.label })),
        defaultValue: 'normal',
      },
      {
        name: 'status', label: 'Status', type: 'select', required: true,
        options: Object.entries(RR_STATUS).map(([k, v]) => ({ value: k, label: v.label })),
        defaultValue: 'open',
      },
      { name: 'needed_by', label: 'Nodig op', type: 'date' },
      { name: 'decision_note', label: 'Besluit-notitie (na MT-bespreking)', type: 'textarea', rows: 2 },
    ];
  }
  if (modal.type === 'link') {
    return [
      { name: 'title', label: 'Titel', type: 'text', required: true },
      { name: 'url', label: 'URL (https://...)', type: 'text', required: true },
      { name: 'description', label: 'Korte omschrijving', type: 'textarea', rows: 2 },
      { name: 'category', label: 'Categorie (bv. "Rapporten", "Tools")', type: 'text' },
      { name: 'icon', label: 'Icon-naam (Material Symbols, bv. "analytics")', type: 'text' },
      { name: 'sort_order', label: 'Volgorde', type: 'number', defaultValue: 0 },
    ];
  }
  if (modal.type === 'action') {
    const propOptions = data.propositions.map((p) => ({ value: p.id, label: p.name }));
    return [
      {
        name: 'proposition_id', label: 'Propositie', type: 'select', required: true,
        options: propOptions, defaultValue: modal.entity?.proposition_id,
      },
      { name: 'title', label: 'Actie', type: 'text', required: true },
      { name: 'description', label: 'Toelichting', type: 'textarea', rows: 2 },
      eigenaarVeld('Eigenaar', personen, huidig),
      {
        name: 'status', label: 'Status', type: 'select', required: true,
        options: [
          { value: 'open', label: 'Open' },
          { value: 'in_progress', label: 'In behandeling' },
          { value: 'done', label: 'Afgerond' },
        ],
        defaultValue: 'open',
      },
      { name: 'deadline', label: 'Deadline', type: 'date' },
      { name: 'sort_order', label: 'Volgorde', type: 'number', defaultValue: 0 },
    ];
  }
  return [];
}
